import ReactEcharts from 'echarts-for-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import {
  formatChartLabelValueForSelectedMeasure,
  formatChartXAxisValue,
  formatChartYAxisValue,
} from '../ChartOptions/Format/NumberFormatter';
import {
  ChartsMapStateToProps,
  ChartsReduxStateProps,
  FormatterValueProps,
} from './ChartsCommonInterfaces';
import { TabTileStateProps2 } from '../../redux/TabTile/TabTilePropsInterfaces';
import { palette } from '../..';
import {
  checkUidPresenceInCheckedMeasureForTooltip,
  limitChartData,
  shouldChartDisplayWithSecurity,
} from '../CommonFunctions/CommonFunctions';
import EmptyQuery from './EmptyQuery';
import {
  toggleMeasureAsLine,
  updateAxisOptions,
  updateChartMargins,
  updateSeriesInAxisOptions,
} from '../../redux/ChartPoperties/ChartControlsActions';
import { RootState } from '../../redux';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import { tooltipStyle } from '../Common/EChartTooltip';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import { compareWithLogicalOrder } from '../ChartOptions/Sort/dataOrdeingUtils';
import {
  AXIS_THRESHOLDS,
  LABEL_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';

const MultiBarChart = ({
  // props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
  softUI,

  //state
  chartControls,
  chartProperties,
  tabTileProps,
}: ChartsReduxStateProps & TabTileStateProps2) => {
  const {
    //dateFormats,
    // selectedMeasureName,
    tabState,
    selectedTab,
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    chartType,
    barLineSettings,
    yAxisOptions,
    sortConfigurations,
  } = useSelector((state: RootState) => ({
    // dateFormats: state.chartControls?.properties[propKey]?.formatOptions?.dateFormats,
    // selectedMeasureName:state.chartControls?.properties[propKey]?.formatOptions?.dateFormats?.selectedMeasureName,
    tabState: state.tabState,
    selectedTab: state.tabTileProps.selectedTabId,
    tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
    chartType: state.chartProperties?.properties[propKey]?.chartType,
    barLineSettings: state.chartControls?.properties[propKey]?.barLineSettings || {
      lineMeasures: [],
    },
    yAxisOptions: state.chartControls.properties[propKey]?.axisOptions.yAxis,
    sortConfigurations: state.chartControls?.properties[propKey]?.sortConfigurations || [],
  }));

  const chartControl: ChartControlsProps = chartControls.properties[propKey];
  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];

  const currentControls = chartControl.barChartControls || {
    manualColor: false,
    nodeValuesColors: null,
  };
  const { manualColor, nodeValuesColors, allSeriesColor } = currentControls;

  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  const [highlightedIndices, setHighlightedIndices] = useState<number[] | null>(null);

  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  const limitedChartData = limitChartData(chartData, 100, chartAxes[3]?.fields[0]?.displayname);

  const processedChartData = limitedChartData.map((item) => {
    return Object.fromEntries(
      Object.entries(item).map(([key, value]) => [
        key,
        value === null
          ? '(Blank)'
          : typeof value === 'boolean'
            ? value
              ? 'True'
              : 'False'
            : value,
      ]),
    );
  });

  const numDimensions = chartProperties.properties[propKey].chartAxes[1].fields.length;

  useEffect(() => {
    if (numDimensions === 2) {
      if (
        chartControl.axisOptions.xAxis[1].position === 'bottom' &&
        chartControl.chartMargin.bottom < 8
      ) {
        dispatch(updateChartMargins(propKey, 'bottom', 8));
      }
      if (
        chartControl.axisOptions.xAxis[1].position === 'top' &&
        chartControl.chartMargin.top < 8
      ) {
        dispatch(updateChartMargins(propKey, 'top', 8));
      }
    }
  }, [numDimensions, chartControl.axisOptions.xAxis[1].position]);

  //------ Auto-remove excess series when measures are removed ------------
  useEffect(() => {
    const seriesList = Object.keys(yAxisOptions);
    const measureFields = chartAxes.find((axis: any) => axis.name === 'Measure')?.fields;
    const maxSeriesAllowed = Math.min(measureFields.length, 4);
    if (measureFields?.length > 0 && seriesList.length > maxSeriesAllowed) {
      const lastSeriesNumber = seriesList[seriesList.length - 1];
      const lastSeriesMeasureList: string[] = yAxisOptions[lastSeriesNumber].measuresList || [];
      lastSeriesMeasureList.forEach((measureUid) => {
        if (measureFields.some((measure: any) => measure.uId === measureUid)) {
          const updatedList = [...yAxisOptions['1'].measuresList];
          updatedList.push(measureUid);
          dispatch(updateAxisOptions(propKey, 'yAxis', 'measuresList', updatedList, '1'));
        }
      });
      dispatch(updateSeriesInAxisOptions(propKey, lastSeriesNumber, 'remove', 'y'));
    }
  }, [chartAxes, yAxisOptions, propKey]);

  useEffect(() => {
    if (chartData.length > 100 && !tabTileProps.showDash) {
      toast.warning(`${messages.chart.excessiveData} 100 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  const xAxisName = (processedChartData[0] && Object.keys(processedChartData[0])[0]) || '';

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });

  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];
  const themeColors = selectedTheme?.colors || [];

  const getSeriesColor = (name: string, index: number) => {
    if (manualColor) {
      if (nodeValuesColors && nodeValuesColors[name]) {
        return nodeValuesColors[name];
      }
      if (allSeriesColor) {
        return allSeriesColor;
      }
    }
    return themeColors[index % themeColors.length];
  };

  const dimensionKeys = chartData[0] && Object.keys(chartData[0]).slice(0, numDimensions).reverse();
  const hasSplitBy = chartProperties.properties[propKey].chartAxes[2]?.fields?.length > 0;

  const getAxisData = (dim: any, index: number) => {
    if (numDimensions > 1 && !hasSplitBy) {
      return processedChartData.map((item) => item[dim]);
    }
    return [...new Set(processedChartData.map((item) => item[dim]))];
  };

  const xAxes = dimensionKeys?.map((dim: any, index: number) => {
    const dataForAxis = getAxisData(dim, index);

    return {
      type: 'category',
      offset: index * 18,
      boundaryGap: Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h ? true : false,
      splitLine: {
        show: chartControl.axisOptions?.xSplitLine,
        lineStyle: {
          color: chartControl.axisOptions.xAxis[1].splitLineColor || '#d3d3d3',
          width: chartControl.axisOptions.xAxis[1].splitLineWidth ?? 1,
          type: chartControl.axisOptions.xAxis[1].splitLineType || 'solid',
        },
      },
      position: chartControl.axisOptions.xAxis[1].position,

      axisLine: {
        show:
          chartControl.axisOptions.xAxis[1].position !== 'hide' &&
          index === 0 &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h, // Only show for first dimension
        onZero:
          chartControl.axisOptions.xAxis[1].position === 'top'
            ? false
            : chartControl.axisOptions.xAxis[1].onZero,
      },

      show: true,

      name:
        index === numDimensions - 1 && Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
          ? chartControl.axisOptions.xAxis[1].name
          : '',
      nameLocation: chartControl.axisOptions.xAxis[1].nameLocation,
      nameGap: chartControl.axisOptions.xAxis[1].nameGap,
      nameTextStyle: {
        fontSize: chartControl.axisOptions.xAxis[1].nameSize,
        color: chartControl.axisOptions.xAxis[1].nameColor,
      },

      axisTick: {
        show:
          chartControl.axisOptions.xAxis[1].position !== 'hide' &&
          index === 0 &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h, // Only show for first dimension
        alignWithLabel: true,
        length:
          chartControl.axisOptions.xAxis[1].position === 'top'
            ? chartControl.axisOptions.xAxis[1].tickSizeTop
            : chartControl.axisOptions.xAxis[1].tickSizeBottom,
      },
      axisLabel: {
        show:
          chartControl.axisOptions.xAxis[1].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        fontSize: chartControl.axisOptions.xAxis[1].fontSize,
        rotate:
          chartControl.axisOptions.xAxis[1].position === 'top'
            ? chartControl.axisOptions.xAxis[1].tickRotationTop
            : chartControl.axisOptions.xAxis[1].tickRotationBottom,
        margin:
          chartControl.axisOptions.xAxis[1].position === 'top'
            ? chartControl.axisOptions.xAxis[1].tickPaddingTop
            : chartControl.axisOptions.xAxis[1].tickPaddingBottom,
        color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
        fontFamily: fontFamily,
        formatter: (value: any, idx: number) => {
          const fieldInAxes = chartAxes
            ?.flatMap((axis: any) => axis?.fields || [])
            ?.find((f: any) => f.displayname === xAxisName);

          const measureUid = fieldInAxes?.uId;
          const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
            measureUid
          ] as any;

          if (labelFormat?.finalDateFormat) {
            return formatChartXAxisValue(chartControl, value, labelFormat);
          }
          // if (xAxisName === dateFormats.selectedMeasureName && dateFormats.finalDateFormat) {
          //   return formatChartXAxisValue(chartControl, value, dateFormats as any);
          // }

          return value;
        },
      },

      data: dataForAxis,
    };
  });

  const getHeightAndWidth = () => {
    var height = 0;
    var width = 0;

    if (
      Math.floor(graphDimension.height) > 230 &&
      Math.floor(graphDimension.width) > 350 &&
      Math.floor(graphDimension.height) < 300
    ) {
      height = 12;
      width = 12;
    } else {
      if (!tabTileProps.showDash) {
        height = chartControl.legendOptions?.symbolHeight;
        width = chartControl.legendOptions?.symbolWidth;
      } else {
        height = 15;
        width = 15;
      }
    }
    return { height: height, width: width };
  };
  const getLegentShowValue = () => {
    var show = false;
    if (Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.multibar.h) {
      show = chartControl.legendOptions?.showLegend;
    } else {
      show = false;
    }

    return show;
  };

  const getTopMarginForLegend = () => {
    var top = '';
    if (chartControl.legendOptions?.position?.top === 'top') {
      top = 'top';
    } else {
      top = '50%';
    }
    return top;
  };

  const getHeightOfChart = () => {
    var height = '';
    if (tabTileProps.showDash) {
      if (Math.floor(graphDimension.height) > 400) {
        height = '80%';
      }
      if (Math.floor(graphDimension.height) < 400 && Math.floor(graphDimension.height) > 300) {
        height = '75%';
      }
      if (Math.floor(graphDimension.height) < 300) {
        height = '70%';
      }
      if (Math.floor(graphDimension.height) < 220) {
        height = '90%';
      }
    } else {
      height = '80%';
    }
    return height;
  };

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!mouseOverInteractivity) return;
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      let matchingIndices = processedChartData
        .map((item, index) => {
          const isMatch = tabState.tabs[selectedTab].hoveredItems?.some(
            (hovered: Record<string, any>) =>
              String(hovered[dimensionKeys[0]]) === String(item[dimensionKeys[0]]),
          );
          return isMatch ? index : -1;
        })
        .filter((index) => index !== -1);

      if (dimensionKeys.length > 1) {
        let matchingIndicesForDim2 = processedChartData
          .map((item, index) => {
            const isMatch = tabState.tabs[selectedTab].hoveredItems?.some(
              (hovered: Record<string, any>) =>
                String(hovered[dimensionKeys[1]]) === String(item[dimensionKeys[1]]),
            );
            return isMatch ? index : -1;
          })
          .filter((index) => index !== -1);
        if (matchingIndicesForDim2.length > 0 && matchingIndices.length > 0) {
          matchingIndices = matchingIndices.filter((ind) => matchingIndicesForDim2.includes(ind));
        } else if (matchingIndicesForDim2.length > 0) {
          matchingIndices = matchingIndicesForDim2;
        }
      }

      setHighlightedIndices(matchingIndices);
    }, 30);

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);
  const hoveredRef = useRef<Record<string, any> | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;
    chartRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (
        params?.seriesName &&
        params?.name &&
        hoveredRef.current?.[dimensionKeys[0]] !== params.name
      ) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry = { [dimensionKeys[0]]: params.name };
          const newEntry_1 = {
            [dimensionKeys[0]]: processedChartData[params.dataIndex][dimensionKeys[0]],
          };
          const newEntry_2 = {
            [dimensionKeys[1]]: processedChartData[params.dataIndex][dimensionKeys[1]],
          };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            if (dimensionKeys.length === 1) {
              const isDuplicate = prev.some((item) => item[dimensionKeys[0]] === params.name);
              if (!isDuplicate) {
                return [...prev, newEntry];
              }
            }
            if (dimensionKeys.length === 2) {
              return [...prev, newEntry_1, newEntry_2];
            }
            return prev;
          });
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.name) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const chartInstance = chartRef.current?.getEchartsInstance?.();
  const currentOption = chartInstance?.getOption();
  const [currentLegendSelected, setCurrentLegendSelected] = useState<any>(
    currentOption?.legend?.[0]?.selected ?? {},
  );

  const shouldRenderAsLine = (measureName: string) => {
    const measureAxis = chartAxes.find((axis: any) => axis.name === 'Measure');
    if (!measureAxis || !barLineSettings?.lineMeasures?.length) return false;
    const measureField = measureAxis.fields?.find(
      (field: any) => field.displayname === measureName,
    );
    if (measureField && barLineSettings.lineMeasures.includes(measureField.uId)) {
      return true;
    }
    return false;
  };

  const yAxisIndexForMeasure = (measureName: string) => {
    let allSeries = Object.keys(yAxisOptions);
    const seriesWithMeasures = allSeries.filter((series: string) => {
      const measuresList = yAxisOptions[series].measuresList || [];
      return measuresList.length > 0;
    });

    const measureAxisField = chartAxes.find((axis: any) => axis.name === 'Measure').fields;
    const measureField = measureAxisField.find((field: any) => field.displayname === measureName);
    const measureUid = measureField?.uId;
    const key =
      seriesWithMeasures.find((key: any) => yAxisOptions[key].measuresList?.includes(measureUid)) ||
      '1';
    const index = seriesWithMeasures.indexOf(key);
    return index;
  };

  const buildYAxes = () => {
    const allSeries = Object.keys(yAxisOptions);
    const offsetStep = 60;

    const seriesWithMeasures = allSeries.filter((series: string) => {
      const measuresList = yAxisOptions[series].measuresList || [];
      return measuresList.length > 0;
    });

    return seriesWithMeasures.map((series: string, index: number) => {
      const position = yAxisOptions[series].position;
      const sameSideCount = seriesWithMeasures
        .slice(0, index)
        .filter((s) => yAxisOptions[s].position === position).length;
      const offset = sameSideCount * offsetStep;

      return buildSingleYAxis(series, offset);
    });
  };

  const buildSingleYAxis = (seriesNumber: string, offset: number = 0) => {
    const position = yAxisOptions[seriesNumber].position;
    return {
      splitLine: {
        show: chartControl.axisOptions?.ySplitLine,
        lineStyle: {
          color: yAxisOptions[seriesNumber].splitLineColor || '#d3d3d3',
          width: yAxisOptions[seriesNumber].splitLineWidth ?? 1,
          type: yAxisOptions[seriesNumber].splitLineType || 'solid',
        },
      },
      min: null,
      max: yAxisOptions[seriesNumber].enableMax ? yAxisOptions[seriesNumber].maxValue : null,

      inverse: chartControl.axisOptions.inverse,

      position: position,
      offset: offset,

      axisLine: {
        show:
          yAxisOptions[seriesNumber].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        onZero: yAxisOptions[seriesNumber].onZero,
      },

      axisTick: {
        show:
          yAxisOptions[seriesNumber].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        alignWithLabel: true,
        length:
          yAxisOptions[seriesNumber].position === 'left'
            ? yAxisOptions[seriesNumber].tickSizeLeft
            : yAxisOptions[seriesNumber].tickSizeRight,
      },

      axisLabel: {
        show:
          yAxisOptions[seriesNumber].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        fontSize: chartControl.axisOptions.yAxis[1].fontSize,
        rotate:
          yAxisOptions[seriesNumber].position === 'left'
            ? yAxisOptions[seriesNumber].tickRotationLeft
            : yAxisOptions[seriesNumber].tickRotationRight,
        margin:
          yAxisOptions[seriesNumber].position === 'left'
            ? yAxisOptions[seriesNumber].tickPaddingLeft
            : yAxisOptions[seriesNumber].tickPaddingRight,
        color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
        fontFamily: fontFamily,
        formatter: (value: number) => {
          var formattedValue: string = formatChartYAxisValue(
            chartControl,
            value,
            undefined,
            seriesNumber,
          );
          return formattedValue;
        },
      },

      show: true,

      name:
        Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
          ? yAxisOptions[seriesNumber].name
          : '',
      nameLocation: yAxisOptions[seriesNumber].nameLocation,
      nameGap: yAxisOptions[seriesNumber].nameGap,
      nameTextStyle: {
        fontSize: yAxisOptions[seriesNumber].nameSize,
        color: yAxisOptions[seriesNumber].nameColor,
      },
    };
  };

  const buildSeries = () => {
    if (!processedChartData || !processedChartData.length) return [];

    const keys = Object.keys(processedChartData[0]);

    const getAxisSettingsForMeasure = (measureDisplayName: string) => {
      const measureAxisField = chartAxes.find((axis: any) => axis.name === 'Measure')?.fields;
      const measureField = measureAxisField?.find(
        (field: any) => field.displayname === measureDisplayName,
      );
      const measureUid = measureField?.uId;
      const allSeries = Object.keys(yAxisOptions);
      const axisKey =
        allSeries.find((key) => yAxisOptions[key].measuresList?.includes(measureUid)) || '1';

      return yAxisOptions[axisKey];
    };

    const getClampedValue = (val: any, axisSettings: any) => {
      if (val === null || val === undefined || isNaN(val)) return val;
      let finalVal = val;
      if (!axisSettings) return val;

      if (axisSettings.enableMax && axisSettings.maxValue !== null && val > axisSettings.maxValue) {
        finalVal = axisSettings.maxValue;
      }
      if (axisSettings.enableMin && axisSettings.minValue !== null && val < axisSettings.minValue) {
        finalVal = axisSettings.minValue;
      }
      return finalVal;
    };

    const computeItemOpacity = (idx: number) => {
      if (tabState.tabs[selectedTab].hoveredPropkey !== propKey) {
        if (!highlightedIndices || !highlightedIndices.length) return 1;
        return highlightedIndices.includes(idx) ? 1 : 0.5;
      }
      return 1;
    };

    let seriesArr: any[] = [];
    const dimensionFields = chartProperties.properties[propKey].chartAxes[1]?.fields || [];

    if (hasSplitBy) {
      barLineSettings?.lineMeasures?.length > 0 && dispatch(toggleMeasureAsLine(propKey, []));
      const splitField = chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
      const measureField = chartProperties.properties[propKey].chartAxes[3]?.fields[0]?.displayname;

      const axisSettings = getAxisSettingsForMeasure(measureField);
      const dimensionFieldsNames = keys.slice(0, numDimensions);

      const makeComboKey = (row: any) =>
        dimensionFieldsNames.map((f) => String(row[f])).join('||@||');

      const uniqueCombos: string[] = [];
      const comboFirstIndex: Record<string, number> = {};
      const comboSampleRow: Record<string, any> = {};

      processedChartData.forEach((row, idx) => {
        const k = makeComboKey(row);
        if (!uniqueCombos.includes(k)) {
          uniqueCombos.push(k);
          comboFirstIndex[k] = idx;
          comboSampleRow[k] = row;
        }
      });

      const splitByFieldObj = chartProperties.properties[propKey].chartAxes[2]?.fields[0];
      const splitFieldConfig = sortConfigurations.find(
        (config: any) => config.fieldId === splitByFieldObj?.uId,
      );

      const groups = Array.from(new Set(processedChartData.map((d) => String(d[splitField])))).sort(
        (a, b) => {
          // Fallback to logical ascending order when no sort config exists
          if (!splitFieldConfig || splitFieldConfig.sortOrder === '') {
            return compareWithLogicalOrder(a, b, true);
          } else if (splitFieldConfig.sortOrder === 'Manual Sort') {
            if (splitFieldConfig.manualSortOrderObject) {
              return (
                (splitFieldConfig.manualSortOrderObject[a] ?? 999999) -
                (splitFieldConfig.manualSortOrderObject[b] ?? 999999)
              );
            } else {
              // Sorting in ascending order if no manual sort order is available
              return compareWithLogicalOrder(a, b, true);
            }
          } else {
            const isAscending = splitFieldConfig.sortOrder === 'Ascending';
            return compareWithLogicalOrder(a, b, isAscending);
          }
        },
      );

      const valMap = new Map<string, number | null>();
      processedChartData.forEach((row) => {
        const k = makeComboKey(row);
        const grp = String(row[splitField]);
        const val = Number(row[measureField]) || 0;
        valMap.set(`${k}__${grp}`, val);
      });

      seriesArr = groups.map((group, i) => {
        const seriesColor = getSeriesColor(String(group), i);

        const data = uniqueCombos.map((k) => {
          const idx = comboFirstIndex[k];
          const rawVal = valMap.get(`${k}__${group}`);
          const plottedVal = rawVal != null ? getClampedValue(rawVal, axisSettings) : null;
          return {
            value: plottedVal,
            originalValue: rawVal,
            itemStyle:
              tabState.tabs[selectedTab].hoveredPropkey !== propKey
                ? { opacity: computeItemOpacity(idx) }
                : {},
          };
        });

        return {
          type: 'bar',
          name: String(group),
          data,
          stack: '',
          emphasis: {
            focus: tabState.tabs[selectedTab].hoveredPropkey !== propKey ? 'none' : 'self',
          },
          blur: { itemStyle: { opacity: 0.5 } },

          itemStyle: softUI
            ? {
                color: seriesColor,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
                shadowBlur: 10,
                shadowOffsetX: 3,
                shadowOffsetY: 3,
              }
            : { color: seriesColor },

          label: {
            show:
              Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
                ? chartControl.labelOptions.showLabel
                : false,
            fontSize: chartControl.labelOptions.fontSize,
            fontFamily: fontFamily,
            color: chartControl.labelOptions.labelColor
              ? chartControl.labelOptions.labelColor
              : selectedTheme?.dark
                ? '#ffffff'
                : '#757474',
            backgroundColor: chartControl.labelOptions.labelBackground
              ? chartControl.labelOptions.labelBackgroundColor
              : 'transparent',
            padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
            borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
            formatter: (value: any) => {
              const val = value.data?.originalValue ?? value.value;
              return formatChartLabelValueForSelectedMeasure(
                chartControls.properties[propKey],
                chartProperties.properties[propKey],
                val,
                measureField,
              );
            },
          },
        };
      });

      if (Array.isArray(xAxes) && xAxes.length > 0) {
        xAxes[0].data = uniqueCombos.map((k) => String(comboSampleRow[k][dimensionKeys[0]]));
        if (xAxes.length > 1) {
          xAxes[1].data = uniqueCombos.map((k) => String(comboSampleRow[k][dimensionKeys[1]]));
        }
      }
    } else {
      const measureFieldsNames =
        chartProperties.properties[propKey].chartAxes[3]?.fields.map((f: any) => f.displayname) ||
        [];

      const isColorByDimension = measureFieldsNames.length === 1 && dimensionFields.length > 0;
      let uniqueDimValues: string[] = [];
      let lastDimFieldName = '';

      if (isColorByDimension) {
        const lastDimObj = dimensionFields[dimensionFields.length - 1];
        lastDimFieldName = lastDimObj.displayname || lastDimObj.fieldname;
        uniqueDimValues = Array.from(
          new Set(processedChartData.map((d) => String(d[lastDimFieldName]))),
        );
      }

      seriesArr = measureFieldsNames.map((measure: string, i: number) => {
        const isLine = shouldRenderAsLine(measure);
        const baseSeriesColor = getSeriesColor(measure, i);
        const axisSettings = getAxisSettingsForMeasure(measure);

        const data = processedChartData.map((item, idx) => {
          let itemColor = baseSeriesColor;
          if (isColorByDimension) {
            const dimValue = String(item[lastDimFieldName]);

            if (manualColor) {
              if (nodeValuesColors && nodeValuesColors[dimValue]) {
                itemColor = nodeValuesColors[dimValue];
              } else if (allSeriesColor) {
                itemColor = allSeriesColor;
              } else {
                itemColor = themeColors[0];
              }
            } else {
              itemColor = themeColors[0];
            }
          }

          const rawVal = item[measure];
          const plottedVal = getClampedValue(rawVal, axisSettings);

          return {
            value: plottedVal,
            originalValue: rawVal,
            itemStyle: {
              color: itemColor,
              ...(tabState.tabs[selectedTab].hoveredPropkey !== propKey
                ? { opacity: computeItemOpacity(idx) }
                : {}),
              ...(softUI
                ? {
                    shadowColor: 'rgba(0, 0, 0, 0.5)',
                    shadowBlur: 10,
                    shadowOffsetX: 3,
                    shadowOffsetY: 3,
                  }
                : {}),
            },
          };
        });

        return {
          type: isLine ? 'line' : 'bar',
          name: measure,
          data,
          stack: '',
          yAxisIndex: yAxisIndexForMeasure(measure),
          emphasis: {
            focus: tabState.tabs[selectedTab].hoveredPropkey !== propKey ? 'none' : 'self',
          },
          blur: { itemStyle: { opacity: 0.5 } },

          itemStyle: !isColorByDimension
            ? softUI
              ? {
                  color: baseSeriesColor,
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                  shadowBlur: 10,
                  shadowOffsetX: 3,
                  shadowOffsetY: 3,
                }
              : { color: baseSeriesColor }
            : {},

          label: {
            show:
              graphDimension.height > 140 && graphDimension.height > 150
                ? chartControl.labelOptions.showLabel
                : false,
            fontSize: chartControl.labelOptions.fontSize,
            fontFamily: fontFamily,
            color: chartControl.labelOptions.labelColor
              ? chartControl.labelOptions.labelColor
              : selectedTheme?.dark
                ? '#ffffff'
                : '#757474',
            backgroundColor: chartControl.labelOptions.labelBackground
              ? chartControl.labelOptions.labelBackgroundColor
              : 'transparent',
            padding: chartControl.labelOptions.labelBackground ? [2, 4] : 0,
            borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
            formatter: (value: any) => {
              const val = value.data?.originalValue ?? value.value;
              return formatChartLabelValueForSelectedMeasure(
                chartControls.properties[propKey],
                chartProperties.properties[propKey],
                val,
                measure,
              );
            },
          },
        };
      });
    }

    return seriesArr;
  };

  const onLegendSelectChanged = (params: any) => {
    setCurrentLegendSelected(params.selected);
  };

  const RenderChart = () => {
    return chartData ? (
      <ReactEcharts
        opts={{ renderer: 'svg' }}
        ref={chartRef}
        onChartReady={handleChartReady}
        onEvents={{
          legendselectchanged: onLegendSelectChanged,
        }}
        // theme={chartControl.colorScheme}
        style={{
          // padding: "5px",
          width: graphDimension.width,
          height: graphDimension.height,
          overflow: 'hidden',
          margin: 'auto',
          border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
        }}
        option={{
          color: selectedTheme?.colors || [],
          backgroundColor: selectedTheme?.background || [],
          animation: false,
          legend: {
            // textStyle :{color : getContrastColor(chartThemes[0].background)},
            type: 'scroll',
            show: getLegentShowValue(),
            itemHeight: getHeightAndWidth().height,
            itemWidth: getHeightAndWidth().width,
            // top: getTopMarginForLegend(),
            itemGap: chartControl.legendOptions?.itemGap,
            // left: "50%",
            left: chartControl.legendOptions?.position?.left,
            // top: chartControl.legendOptions?.position?.top,
            top:
              chartControl.legendOptions?.position?.top !== 'bottom'
                ? chartControl.legendOptions?.position?.top
                : null,
            bottom: chartControl.legendOptions?.position?.top === 'bottom' ? 0 : null,
            orient: chartControl.legendOptions?.orientation,
            selected: currentLegendSelected,
            textStyle: {
              color: chartThemes[0]?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
            },
          },
          grid: {
            left:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.chartMargin.left + '%',
            right:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.chartMargin.right + '%',
            top:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.legendOptions?.position?.top === 'top'
                  ? chartControl.chartMargin.top + 5 + '%'
                  : chartControl.chartMargin.top + '%',
            bottom:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.legendOptions?.position?.top === 'bottom'
                  ? (graphDimension.height * chartControl.chartMargin.bottom) / 100 + 35
                  : chartControl.chartMargin.bottom + '%',
          },
          tooltip: {
            show: isTooltipEnabled,
            useHTML: true,
            trigger: 'item',
            backgroundColor: 'white',
            borderWidth: 1,
            padding: 0,
            textStyle: {
              color: '#333',
              fontSize: 16,
            },
            appendToBody: true,
            extraCssText:
              'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
            formatter: function (params: any) {
              //----select measure field which is hovered and dimension field(s)----//
              const hasSplitBy =
                chartProperties.properties[propKey].chartAxes[2]?.fields?.length > 0;

              const dimensionAxis = chartAxes[1];
              const dimensionAxisDisplayNames =
                dimensionAxis?.fields?.map((field: any) => field.displayname) || [];

              let row: any = null;

              const extraResponses = Array.isArray(tooltipFormats?.extraMeasureFieldsResponses)
                ? tooltipFormats.extraMeasureFieldsResponses
                : [];

              if (hasSplitBy) {
                const splitField =
                  chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
                const measureField =
                  chartProperties.properties[propKey].chartAxes[3]?.fields[0]?.displayname;

                const makeComboKey = (r: any) =>
                  dimensionAxisDisplayNames.map((f: string) => String(r[f])).join('||@||');

                const uniqueComboKeys: string[] = [];
                const uniqueComboSampleRows: any[] = [];
                processedChartData.forEach((r: any) => {
                  const k = makeComboKey(r);
                  if (!uniqueComboKeys.includes(k)) {
                    uniqueComboKeys.push(k);
                    uniqueComboSampleRows.push(r);
                  }
                });

                const sample = uniqueComboSampleRows[params.dataIndex];

                row =
                  sample &&
                  processedChartData.find(
                    (r: any) =>
                      dimensionAxisDisplayNames.every(
                        (f: string) => String(r[f]) === String(sample[f]),
                      ) && String(r[splitField]) === String(params.seriesName),
                  );

                if (!row) row = sample;

                if (!row) return '';

                let hoveredFieldname = measureField;

                const tempData: Record<string, any> = {
                  ...Object.fromEntries(
                    dimensionAxisDisplayNames.map((key: any) => [key, row[key]]),
                  ),
                  [splitField]: row?.[splitField],
                  [measureField]: row?.[measureField],
                };

                //---------get list of non hovered field -------//
                const allFieldName = [...dimensionAxisDisplayNames, hoveredFieldname, splitField];

                const nonHoveredFieldList = [
                  ...new Set(
                    processedChartData
                      .map((data: any) =>
                        Object.keys(data).filter((key: string) => !allFieldName.includes(key)),
                      )
                      .flat(),
                  ),
                ];

                //----------remove unchecked measure or any other field value of type date inside tooltip----------//
                const _data: { [key: string]: string | number } = {};
                Object.keys(tempData).forEach((key: string) => {
                  const resp = checkUidPresenceInCheckedMeasureForTooltip(
                    chartAxes,
                    key,
                    tooltipFormats?.checkedMeasure,
                  );
                  if (resp) {
                    _data[key] = tempData[key];
                  }
                });
                let mergedData = { ..._data };

                //-- send extra responses in _data so that it maps and shows all key value pairs including extra measure (if any)--//
                //----get extra response corresponding to all dimensions and create tooltip data----//
                if (extraResponses.length > 0) {
                  const lookupKeys = [...dimensionAxisDisplayNames, splitField];
                  const tooltipData = extraResponses.find((obj: any) =>
                    lookupKeys.every((dimensionKey: string) => {
                      const dimensionValue = tempData[dimensionKey as string];
                      return (
                        dimensionValue !== undefined &&
                        String(obj[dimensionKey as string]) === String(dimensionValue as string)
                      );
                    }),
                  );

                  let filteredFields = tooltipData ? { ...tooltipData } : {};
                  if (filteredFields) {
                    lookupKeys.forEach((dimensionKey: string) => {
                      delete filteredFields[dimensionKey];
                    });
                  }

                  if (tooltipData && Object.keys(filteredFields).length > 0) {
                    const isHoveredFieldChecked = checkUidPresenceInCheckedMeasureForTooltip(
                      chartAxes,
                      hoveredFieldname,
                      tooltipFormats?.checkedMeasure,
                    );

                    if (!isHoveredFieldChecked) {
                      delete filteredFields[hoveredFieldname];
                    }

                    Object.keys(filteredFields).forEach((key) => {
                      if (
                        nonHoveredFieldList.includes(key) ||
                        !checkUidPresenceInCheckedMeasureForTooltip(
                          chartAxes,
                          key,
                          tooltipFormats?.checkedMeasure,
                        )
                      ) {
                        delete filteredFields[key];
                      }
                    });

                    mergedData = { ...mergedData, ...filteredFields };
                  }
                }
                const extendedDimensionNames = [...dimensionAxisDisplayNames, splitField];

                return tooltipStyle(
                  params.color,
                  mergedData,
                  tooltipFormats,
                  chartAxes,
                  extendedDimensionNames,
                  undefined,
                  undefined,
                  fontFamily,
                );
              } else {
                // Non-split charts (works for 1D and 2D) => row aligns with dataIndex
                row = processedChartData[params.dataIndex];
                if (!row) return '';

                let hoveredFieldname = params.seriesName;
                const allFieldName = [...dimensionAxisDisplayNames, hoveredFieldname];
                const tempData: Record<string, any> = {};
                allFieldName.forEach((key: string) => (tempData[key] = row[key]));

                const nonHoveredFieldList = [
                  ...new Set(
                    processedChartData
                      .map((data: any) =>
                        Object.keys(data).filter((key: string) => !allFieldName.includes(key)),
                      )
                      .flat(),
                  ),
                ];

                const _data: { [key: string]: string | number } = {};
                Object.keys(tempData).forEach((key: string) => {
                  const resp = checkUidPresenceInCheckedMeasureForTooltip(
                    chartAxes,
                    key,
                    tooltipFormats?.checkedMeasure,
                  );
                  if (resp) {
                    _data[key] = tempData[key];
                  }
                });
                let mergedData = { ..._data };

                const extraResponses = tooltipFormats?.extraMeasureFieldsResponses || [];
                if (tooltipFormats && extraResponses.length > 0) {
                  const tooltipData = extraResponses.find((obj: any) => {
                    return dimensionAxisDisplayNames.every((dimensionKey: string) => {
                      const dimensionValue = tempData[dimensionKey as string];
                      return (
                        String(obj[dimensionKey as string]) === String(dimensionValue as string)
                      );
                    });
                  });

                  let filteredFields = tooltipData ? { ...tooltipData } : {};
                  if (filteredFields) {
                    dimensionAxisDisplayNames.forEach((dimensionKey: string) => {
                      delete filteredFields[dimensionKey];
                    });
                  }

                  if (tooltipData && Object.keys(filteredFields).length > 0) {
                    const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
                      chartAxes,
                      hoveredFieldname,
                      tooltipFormats?.checkedMeasure,
                    );

                    if (!isPresent) {
                      delete filteredFields[hoveredFieldname];
                    }

                    for (let key in filteredFields) {
                      if (nonHoveredFieldList.includes(key)) {
                        delete filteredFields[key];
                      }
                    }
                    mergedData = { ...mergedData, ...filteredFields };
                  }
                }

                return tooltipStyle(
                  params.color,
                  mergedData,
                  tooltipFormats,
                  chartAxes,
                  dimensionAxisDisplayNames,
                  undefined,
                  undefined,
                  fontFamily,
                );
              }
            },
          },

          dataset: {
            // dimensions: Object.keys(processedChartData[0]),
            source: processedChartData,
          },

          xAxis: xAxes,

          yAxis: buildYAxes(),
          series: buildSeries(),
        }}
      />
    ) : null;
  };

  const emptyQueryCondition = useMemo(() => {
    return (
      shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
      chartData.length < 1 ||
      numDimensions < 1 ||
      !!chartControl?.chartErrorDetails
    );
  }, [chartControl?.chartErrorDetails, chartData, chartType, numDimensions]);
  // console.log(emptyQueryCondition, propKey, 'MultiBarChart');
  // console.log(emptyQueryCondition, propKey, 'MultiBarChart');

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};
const mapStateToProps = (state: ChartsMapStateToProps & TabTileStateProps2, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
    tabTileProps: state.tabTileProps,
  };
};

export default connect(mapStateToProps, null)(MultiBarChart);
